import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from '../../../contexts/SnackbarContext';
import { TipShareDistributionConfig, TipShareContributionConfig, TipShareShift } from '../../types';
import {
  TipShareFormData,
  DistributionType,
  AutoDistributionMethod,
  TipShareDistributionRequest,
  TipShareContributionRequest,
} from '../types';
import {
  useSaveTipShareDistributionConfigMutation,
  useUpdateTipShareDistributionConfigMutation,
  useSaveTipShareContributionConfigMutation,
  useUpdateTipShareContributionConfigMutation,
} from '../../requests/mutations';

interface UseTipShareSaveParams {
  distributionConfig: TipShareDistributionConfig | null | undefined;
  contributionConfig: TipShareContributionConfig | null | undefined;
  shiftsData: TipShareShift[];
  enableTipShareDistribution: boolean;
  enableTipShareContribution: boolean;
  distributionType: DistributionType | '';
  contributionPercentage: number;
  isDirty: boolean;
}

interface UseTipShareSaveReturn {
  isSaving: boolean;
  isSaveEnabled: boolean;
  saveTooltip: string;
  onSubmit: (data: TipShareFormData) => Promise<void>;
}

type SaveOperation = { promise: Promise<unknown>; type: 'distribution' | 'contribution' };

/**
 * Manages all save logic for TipShareSettingsPage:
 * - Computes whether the save button should be enabled.
 * - Orchestrates parallel POST/PATCH for distribution + contribution configs.
 * - Reports per-operation success, partial failure, or full failure via snackbar.
 */
export default function useTipShareSave({
  distributionConfig,
  contributionConfig,
  shiftsData,
  enableTipShareDistribution,
  enableTipShareContribution,
  distributionType,
  contributionPercentage,
  isDirty,
}: UseTipShareSaveParams): UseTipShareSaveReturn {
  const { t } = useTranslation();
  const { setSnackbarState } = useSnackbar();

  const saveDistributionMutation = useSaveTipShareDistributionConfigMutation();
  const updateDistributionMutation = useUpdateTipShareDistributionConfigMutation();
  const saveContributionMutation = useSaveTipShareContributionConfigMutation();
  const updateContributionMutation = useUpdateTipShareContributionConfigMutation();

  const [isSaving, setIsSaving] = useState(false);

  // Derive whether the form has a valid, saveable configuration
  const canSave = useMemo(() => {
    // If distribution is enabled, a type MUST be selected — contribution alone cannot unblock this
    if (
      enableTipShareDistribution &&
      distributionType !== DistributionType.AUTOMATIC &&
      distributionType !== DistributionType.MANUAL
    ) {
      return false;
    }

    if (enableTipShareContribution) {
      // Contribution alone can never enable save — distribution must already be valid
      if (!enableTipShareDistribution) {
        return false;
      }
      // Contribution is enabled but percentage is outside valid range
      if (contributionPercentage < 0.01 || contributionPercentage > 100) {
        return false;
      }
      // Contribution percentage must not have more than 2 decimal places
      if (!/^\d+(\.\d{1,2})?$/.test(String(contributionPercentage))) {
        return false;
      }
    }

    return enableTipShareDistribution;
  }, [enableTipShareDistribution, distributionType, enableTipShareContribution, contributionPercentage]);

  // Disable Save for Automatic distribution when no shifts have been added yet
  const isSaveEnabled = canSave && isDirty && !(distributionType === DistributionType.AUTOMATIC && !shiftsData.length);

  // Tooltip shown on the disabled Save button explaining why it is disabled
  const saveTooltip = useMemo(() => {
    if (isSaveEnabled) return '';
    if (enableTipShareDistribution && !distributionType) {
      return t('tipShare.saveTooltipSelectMethod');
    }
    if (enableTipShareContribution && (contributionPercentage < 0.01 || contributionPercentage === 0)) {
      return t('tipShare.saveTooltipEnterPercentage');
    }
    return '';
  }, [
    isSaveEnabled,
    enableTipShareDistribution,
    distributionType,
    enableTipShareContribution,
    contributionPercentage,
    t,
  ]);

  const onSubmit = useCallback(
    async (data: TipShareFormData) => {
      // Block save when Automatic is selected but no shifts are available (undefined or empty)
      if (data.distributionType === DistributionType.AUTOMATIC && !shiftsData.length) {
        setSnackbarState({ open: true, message: t('labor.tipShare.validation.shiftsRequired'), color: 'error' });
        return;
      }

      setIsSaving(true);
      try {
        const operations: SaveOperation[] = [];

        if (data.enableTipShareDistribution && data.distributionType) {
          const distributionRequest: TipShareDistributionRequest = {
            isActive: true,
            distributionType: data.distributionType as DistributionType,
            distributionMethod:
              data.distributionType === DistributionType.AUTOMATIC && data.distributionMethod
                ? (data.distributionMethod as AutoDistributionMethod)
                : undefined,
            recalculateOnPosImport: data.recalculateOnPosImport ?? false,
            // autoBalanceGridDistribution is only meaningful for Manual; always false for Automatic
            autoBalanceGridDistribution:
              data.distributionType === DistributionType.MANUAL ? data.autoBalanceGridDistribution ?? false : false,
          };

          operations.push({
            promise: distributionConfig?.id
              ? updateDistributionMutation.mutateAsync({ ...distributionRequest, id: distributionConfig.id })
              : saveDistributionMutation.mutateAsync(distributionRequest),
            type: 'distribution',
          });
        }

        if (data.enableTipShareContribution) {
          const contributionRequest: TipShareContributionRequest = {
            isActive: true,
            contributionPercentage: data.contributionPercentage,
            includeComps: data.includeComps,
            includePromotions: data.includePromotions,
            includeTax: data.includeTax,
          };

          operations.push({
            promise: contributionConfig?.id
              ? updateContributionMutation.mutateAsync({ ...contributionRequest, id: contributionConfig.id })
              : saveContributionMutation.mutateAsync(contributionRequest),
            type: 'contribution',
          });
        } else if (contributionConfig?.id) {
          // Contribution toggle is OFF but backend record exists — deactivate it
          operations.push({
            promise: updateContributionMutation.mutateAsync({ id: contributionConfig.id, isActive: false }),
            type: 'contribution',
          });
        }

        const results = await Promise.allSettled(operations.map((op) => op.promise));
        const failures = results.filter((r) => r.status === 'rejected');
        const successes = results.filter((r) => r.status === 'fulfilled');

        if (failures.length === 0) {
          setSnackbarState({ open: true, message: t('tipShare.saveSuccess'), color: 'success' });
        } else if (successes.length === 0) {
          setSnackbarState({ open: true, message: t('tipShare.saveError'), color: 'error' });
        } else {
          const failedTypes = failures
            .map((_, i) => t(`tipShare.${operations[results.indexOf(failures[i])].type}.title`))
            .join(', ');
          setSnackbarState({ open: true, message: t('tipShare.partialSaveError', { failedTypes }), color: 'warning' });
        }
      } catch {
        setSnackbarState({ open: true, message: t('tipShare.saveError'), color: 'error' });
      } finally {
        setIsSaving(false);
      }
    },
    [
      distributionConfig,
      contributionConfig,
      updateDistributionMutation,
      saveDistributionMutation,
      updateContributionMutation,
      shiftsData,
      saveContributionMutation,
      setSnackbarState,
      t,
    ]
  );

  return { isSaving, isSaveEnabled, saveTooltip, onSubmit };
}
